import { isNil } from 'lodash';

export const REDACTION_STATUS_TO_DO = 'to do';
export const REDACTION_STATUS_DRAFT = 'draft';
export const REDACTION_STATUS_PENDING_REVIEW = 'pending review';
export const REDACTION_STATUS_CORRECTION_NEEDED = 'correction needed';
export const REDACTION_STATUS_COMPLETE = 'complete';

export const REDACTION_STATUS_FILTERS = [
  REDACTION_STATUS_TO_DO,
  REDACTION_STATUS_DRAFT,
  REDACTION_STATUS_PENDING_REVIEW,
  REDACTION_STATUS_CORRECTION_NEEDED,
  REDACTION_STATUS_COMPLETE,
] as const;
export const REDACTION_STATUS_FILTER_SET: ReadonlySet<string> = new Set(
  REDACTION_STATUS_FILTERS
);

export type RedactionStatusFilter = (typeof REDACTION_STATUS_FILTERS)[number];

/** Built-ins pinned to the top two slots; everything else is reorderable (VE-24546 #5). */
export const PINNED_REDACTION_STATUS_FILTERS = [
  REDACTION_STATUS_TO_DO,
  REDACTION_STATUS_DRAFT,
] as const;

export const isPinnedRedactionStatusFilter = (value: unknown): boolean =>
  typeof value === 'string' &&
  (PINNED_REDACTION_STATUS_FILTERS as readonly string[]).includes(value);

/** Well-known filters; same values as `RedactionStatusFilter` today. */
export type WellKnownRedactionStatusFilter = RedactionStatusFilter;

/**
 * Canonical status key: always one of the well-known filters today; in future,
 * customer-defined keys (strings not in `REDACTION_STATUS_FILTERS`).
 */
export type RedactionStatusKey = string;

export const REDACTION_STATUS_LABEL_TO_DO = 'To Do';
export const REDACTION_STATUS_LABEL_DRAFT = 'Draft';
export const REDACTION_STATUS_LABEL_PENDING_REVIEW = 'Pending Review';
export const REDACTION_STATUS_LABEL_REVIEW = 'Review';
export const REDACTION_STATUS_LABEL_CORRECTION_NEEDED = 'Correction Needed';
export const REDACTION_STATUS_LABEL_CORRECTION = 'Correction';
export const REDACTION_STATUS_LABEL_COMPLETE = 'Complete';
/** Display-only label when a TDO still carries a deleted custom statusKey. */
export const REDACTION_STATUS_LABEL_UNKNOWN = 'Unknown';

/**
 * Provisional chip color for UNKNOWN (Derrick: background TBD).
 * Blue-grey — distinct from the built-in To Do gray in every palette
 * (original #B0BEC5, updated #D4D4D4, dark #757575).
 */
export const UNKNOWN_REDACTION_STATUS_COLOR = '#78909C';

export const REDACTION_STATUS_LABELS = [
  REDACTION_STATUS_LABEL_TO_DO,
  REDACTION_STATUS_LABEL_DRAFT,
  REDACTION_STATUS_LABEL_PENDING_REVIEW,
  REDACTION_STATUS_LABEL_CORRECTION_NEEDED,
  REDACTION_STATUS_LABEL_COMPLETE,
] as const;

export const REDACTION_STATUS_LABELS_SHORT = [
  REDACTION_STATUS_LABEL_REVIEW,
  REDACTION_STATUS_LABEL_CORRECTION,
] as const;

export type RedactionStatusLabel = (typeof REDACTION_STATUS_LABELS)[number];
export type RedactionStatusLabelShort =
  | (typeof REDACTION_STATUS_LABELS)[number]
  | (typeof REDACTION_STATUS_LABELS_SHORT)[number];

/**
 * Media categories a (custom) status can apply to. Matches the Statuses
 * prototype multi-select (Image / Document / Audio & Video) — VE-24546.
 */
export const REDACTION_STATUS_TYPE_IMAGE = 'image';
export const REDACTION_STATUS_TYPE_DOCUMENT = 'document';
export const REDACTION_STATUS_TYPE_AUDIO_VIDEO = 'audioVideo';

// Drawer multi-select order (VE-24546): Audio & Video, Image, Document.
export const REDACTION_STATUS_TYPES = [
  REDACTION_STATUS_TYPE_AUDIO_VIDEO,
  REDACTION_STATUS_TYPE_IMAGE,
  REDACTION_STATUS_TYPE_DOCUMENT,
] as const;

export type RedactionStatusType = (typeof REDACTION_STATUS_TYPES)[number];

export const isRedactionStatusType = (
  value: unknown
): value is RedactionStatusType =>
  typeof value === 'string' &&
  REDACTION_STATUS_TYPES.includes(value as RedactionStatusType);

/** i18n message id for each status type (see common-app/i18n/messages). */
export const REDACTION_STATUS_TYPE_MESSAGE_IDS: Readonly<
  Record<RedactionStatusType, string>
> = {
  [REDACTION_STATUS_TYPE_IMAGE]: 'statusTypeImage',
  [REDACTION_STATUS_TYPE_DOCUMENT]: 'statusTypeDocument',
  [REDACTION_STATUS_TYPE_AUDIO_VIDEO]: 'statusTypeAudioVideo',
};

/**
 * Parses stored media-type values from an SDO row. Accepts:
 * - `statusTypes: string[]`
 * - legacy single `statusType: string`
 * - comma-joined display strings ("Image, Document") as a last resort
 */
export const parseRedactionStatusTypes = (
  value: unknown
): RedactionStatusType[] => {
  const fromToken = (token: unknown): RedactionStatusType | undefined => {
    if (typeof token !== 'string') {
      return undefined;
    }
    const trimmed = token.trim();
    if (isRedactionStatusType(trimmed)) {
      return trimmed;
    }
    const normalized = trimmed.toLowerCase();
    if (normalized === 'image') {
      return REDACTION_STATUS_TYPE_IMAGE;
    }
    if (normalized === 'document') {
      return REDACTION_STATUS_TYPE_DOCUMENT;
    }
    if (
      normalized === 'audio & video' ||
      normalized === 'audio and video' ||
      normalized === 'audio video' ||
      normalized.replace(/\s+/g, '') === ['audio', 'video'].join('')
    ) {
      return REDACTION_STATUS_TYPE_AUDIO_VIDEO;
    }
    return undefined;
  };

  if (Array.isArray(value)) {
    return value
      .map(fromToken)
      .filter((type): type is RedactionStatusType => type !== undefined)
      .filter((type, index, arr) => arr.indexOf(type) === index);
  }
  if (typeof value === 'string') {
    if (isRedactionStatusType(value)) {
      return [value];
    }
    return value
      .split(',')
      .map(fromToken)
      .filter((type): type is RedactionStatusType => type !== undefined)
      .filter((type, index, arr) => arr.indexOf(type) === index);
  }
  return [];
};

/** Dark-grey text/icon color used on bright status backgrounds (VE-24546 #6). */
export const REDACTION_STATUS_DARK_TEXT_COLOR = '#1f2937';

/** Legacy YIQ threshold (adaptive flag off). Chosen above the To Do gray of the day (#B0BEC5 ≈ 187) so all 5 presets kept white text; the current To Do #D4D4D4 (≈ 212) exceeds it and gets dark text either way (VE-24546 #6). */
export const REDACTION_STATUS_DARK_TEXT_LUMINANCE_THRESHOLD = 190;

/**
 * YIQ thresholds used when USE_ADAPTIVE_STATUS_TEXT is on, by surface tone.
 *
 * On light (white-page) surfaces — status table, media tiles, folder view,
 * the drawer — 170 sits just above the warm mid-tones (Orange ≈ 161, Rose
 * ≈ 163) so they keep white text, while the To Do gray (≈ 212) flips to
 * dark. On the dark editor pages the same warm pills read better with dark
 * text, so 150 pulls Orange/Rose below the line too (6.5:1 / 5.8:1) —
 * the pairing the editor headers always hand-tuned. Draft (≈ 124) and
 * Complete (≈ 122) keep white text under both.
 */
export const REDACTION_STATUS_ADAPTIVE_LUMINANCE_THRESHOLD = 170;
export const REDACTION_STATUS_ADAPTIVE_LUMINANCE_THRESHOLD_DARK_SURFACE = 150;

export interface ContrastIconColorOptions {
  /**
   * True on dark-background surfaces (the editor headers): applies the lower
   * threshold so the warm mid-tone pills flip to dark text too.
   */
  onDarkSurface?: boolean;
}

/**
 * Contrasting checkmark/text color for a badge/swatch background hex: dark grey
 * on bright backgrounds, white on dark. Accepts `#abc` and `#aabbcc`.
 */
export const getContrastIconColor = (
  hex: string,
  options?: ContrastIconColorOptions
): string => {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) {
    return '#fff';
  }
  const normalized =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((c) => c + c)
          .join('')
      : match[1];
  const rgb = parseInt(normalized, 16);
  const luminance =
    (((rgb >> 16) & 255) * 299 + ((rgb >> 8) & 255) * 587 + (rgb & 255) * 114) /
    1000;
  const threshold = USE_ADAPTIVE_STATUS_TEXT
    ? options?.onDarkSurface
      ? REDACTION_STATUS_ADAPTIVE_LUMINANCE_THRESHOLD_DARK_SURFACE
      : REDACTION_STATUS_ADAPTIVE_LUMINANCE_THRESHOLD
    : REDACTION_STATUS_DARK_TEXT_LUMINANCE_THRESHOLD;
  return luminance >= threshold ? REDACTION_STATUS_DARK_TEXT_COLOR : '#fff';
};

/** Pill/badge text color; alias of `getContrastIconColor` for consistency (VE-24546 #6). */
export const getRedactionStatusPillTextColor = getContrastIconColor;

/**
 * Built-in statuses whose pill background (Gray / Orange / Rose) is light enough
 * to need dark text.
 *
 * Built-in pills use this fixed pairing rather than the contrast helper above so
 * the five product colors keep the exact text color design signed off on. Custom
 * and orphan pills carry an arbitrary org-picked background, so those go through
 * `getRedactionStatusPillTextColor` instead.
 *
 * Shared because both editor headers computed it independently and had already
 * drifted apart in form — one built the set from these constants, the other
 * hardcoded the same three strings as literals, which silently breaks the day a
 * filter key is renamed (VE-24546).
 */
export const DARK_TEXT_REDACTION_STATUS_FILTERS: ReadonlySet<string> = new Set([
  REDACTION_STATUS_TO_DO,
  REDACTION_STATUS_PENDING_REVIEW,
  REDACTION_STATUS_CORRECTION_NEEDED,
]);

/** Pill text color paired with the light built-in backgrounds above. */
export const REDACTION_STATUS_PILL_DARK_TEXT_COLOR = '#182025';

/**
 * True when a *built-in* status filter key needs dark pill text. The
 * hand-tuned set above describes the palettes with light gray/orange/rose
 * (original and updated), so it stands down under the dark palette, whose
 * mid-tone hues are designed for white text.
 */
export const isDarkTextRedactionStatusFilter = (
  statusKey: string | undefined
): boolean =>
  ACTIVE_REDACTION_STATUS_PALETTE !== 'dark' &&
  !!statusKey &&
  DARK_TEXT_REDACTION_STATUS_FILTERS.has(statusKey);

/**
 * When true, BUILT-IN status pills derive their text color from their
 * background on every surface — the same contrast rule custom/orphan pills
 * already use — instead of each surface's fixed scheme (forced white on
 * MediaTile and the folder view; the hand-tuned
 * DARK_TEXT_REDACTION_STATUS_FILTERS pairing in the editor headers). The
 * rule is surface-toned (see the two thresholds above). Under the updated
 * palette: on light pages (threshold 170) only the To Do gray flips to dark
 * text; on the dark editor pages (threshold 150) Orange and Rose flip too
 * (≥ 5.8:1), reproducing the editors' old hand-tuned pairing. The dark
 * palette keeps white on all five on every surface (its orange #F57C00,
 * YIQ ≈ 146, sits under both thresholds; white there is 2.7:1). Flip to
 * false to restore the legacy per-surface schemes.
 */
export const USE_ADAPTIVE_STATUS_TEXT = true;

/**
 * Text color for a BUILT-IN status pill when USE_ADAPTIVE_STATUS_TEXT is on;
 * undefined when it's off, in which case callers keep their surface's legacy
 * scheme (forced white, or the dark-text set above).
 */
export const getAdaptiveBuiltInStatusTextColor = (
  colorHex: string,
  options?: ContrastIconColorOptions
): string | undefined =>
  USE_ADAPTIVE_STATUS_TEXT
    ? getRedactionStatusPillTextColor(colorHex, options)
    : undefined;

interface StatusDefinition {
  filter: RedactionStatusFilter;
  label: RedactionStatusLabel;
  labelShort?: RedactionStatusLabelShort;
  messageId:
    | 'toDo'
    | 'draft'
    | 'pendingReview'
    | 'correctionNeeded'
    | 'complete';
  shortMessageId?: 'review' | 'correction';
  colorHex: string;
  colorName:
    | 'Gray'
    | 'Light blue'
    | 'Blue'
    | 'Orange'
    | 'Rose'
    | 'Red'
    | 'Green';
  enabledByDefault: boolean;
  required?: boolean;
  lockEditing: boolean;
  lockEditingAvailable: boolean;
}

export interface RedactionStatusSetting {
  enabled: boolean;
  lockEditing: boolean;
  /**
   * Statuses-page position, shared with custom `displayOrder` (VE-24546 #5).
   * Absent on legacy rows (defaulted by `normalizeRedactionStatusSettings`);
   * pinned To Do / Draft always resolve to 0 / 1.
   */
  displayOrder?: number;
}

export type RedactionStatusSettings = Record<
  RedactionStatusFilter,
  RedactionStatusSetting
>;

interface ResolvedStatusDefinition extends StatusDefinition {
  enabled: boolean;
}

export type RedactionStatusPaletteName = 'original' | 'updated' | 'dark';

/**
 * Candidate palettes for the five built-in statuses (VE-24546 color
 * experiment). ACTIVE_REDACTION_STATUS_PALETTE below picks one; the choice
 * flows from the definitions to every surface that renders a built-in status
 * (status table, media tiles, folder view, both editor headers, drawer
 * preset swatches).
 *
 * - original: the shipped palette, untouched.
 * - updated: the original hues with one change — To Do lightened to #D4D4D4
 *   so it reads as "not started" and carries dark text at 9.9:1. Orange and
 *   Rose flip to dark text under the adaptive rule (6.5:1 / 5.8:1);
 *   Draft/Complete keep white at 4.0:1 / 3.3:1 (under AA's 4.5 — accepted;
 *   the nearest compliant shades are #4E77A3 / #338437).
 * - dark: the Statuses-prototype hues (Legacy > Redact > Statuses) — mid
 *   gray and Material blue/orange/red/green, all designed for white text.
 */
export const REDACTION_STATUS_PALETTES: Record<
  RedactionStatusPaletteName,
  Record<RedactionStatusFilter, string>
> = {
  original: {
    [REDACTION_STATUS_TO_DO]: '#B0BEC5',
    [REDACTION_STATUS_DRAFT]: '#5B83AD',
    [REDACTION_STATUS_PENDING_REVIEW]: '#ff9100',
    [REDACTION_STATUS_CORRECTION_NEEDED]: '#f2827f',
    [REDACTION_STATUS_COMPLETE]: '#43A047',
  },
  updated: {
    [REDACTION_STATUS_TO_DO]: '#D4D4D4',
    [REDACTION_STATUS_DRAFT]: '#5B83AD',
    [REDACTION_STATUS_PENDING_REVIEW]: '#ff9100',
    [REDACTION_STATUS_CORRECTION_NEEDED]: '#f2827f',
    [REDACTION_STATUS_COMPLETE]: '#43A047',
  },
  dark: {
    [REDACTION_STATUS_TO_DO]: '#757575',
    [REDACTION_STATUS_DRAFT]: '#1976D2',
    [REDACTION_STATUS_PENDING_REVIEW]: '#F57C00',
    [REDACTION_STATUS_CORRECTION_NEEDED]: '#D32F2F',
    [REDACTION_STATUS_COMPLETE]: '#388E3C',
  },
};

/** Which palette the app renders. (Asserted, not annotated, so comparisons against the other palette names don't narrow to "no overlap" errors.) */
export const ACTIVE_REDACTION_STATUS_PALETTE =
  'updated' as RedactionStatusPaletteName;

const ACTIVE_PALETTE =
  REDACTION_STATUS_PALETTES[ACTIVE_REDACTION_STATUS_PALETTE];

export const REDACTION_STATUS_DEFINITIONS: ReadonlyArray<StatusDefinition> = [
  {
    filter: REDACTION_STATUS_TO_DO,
    label: REDACTION_STATUS_LABEL_TO_DO,
    labelShort: REDACTION_STATUS_LABEL_TO_DO,
    messageId: 'toDo',
    colorHex: ACTIVE_PALETTE[REDACTION_STATUS_TO_DO],
    colorName: 'Gray',
    enabledByDefault: true,
    required: true,
    lockEditing: false,
    lockEditingAvailable: false,
  },
  {
    filter: REDACTION_STATUS_DRAFT,
    label: REDACTION_STATUS_LABEL_DRAFT,
    labelShort: REDACTION_STATUS_LABEL_DRAFT,
    messageId: 'draft',
    colorHex: ACTIVE_PALETTE[REDACTION_STATUS_DRAFT],
    colorName: 'Blue',
    enabledByDefault: true,
    lockEditing: false,
    lockEditingAvailable: false,
  },
  {
    filter: REDACTION_STATUS_PENDING_REVIEW,
    label: REDACTION_STATUS_LABEL_PENDING_REVIEW,
    labelShort: REDACTION_STATUS_LABEL_REVIEW,
    messageId: 'pendingReview',
    shortMessageId: 'review',
    colorHex: ACTIVE_PALETTE[REDACTION_STATUS_PENDING_REVIEW],
    colorName: 'Orange',
    enabledByDefault: true,
    lockEditing: true,
    lockEditingAvailable: true,
  },
  {
    filter: REDACTION_STATUS_CORRECTION_NEEDED,
    label: REDACTION_STATUS_LABEL_CORRECTION_NEEDED,
    labelShort: REDACTION_STATUS_LABEL_CORRECTION,
    messageId: 'correctionNeeded',
    shortMessageId: 'correction',
    colorHex: ACTIVE_PALETTE[REDACTION_STATUS_CORRECTION_NEEDED],
    colorName: ACTIVE_REDACTION_STATUS_PALETTE === 'dark' ? 'Red' : 'Rose',
    enabledByDefault: true,
    lockEditing: false,
    lockEditingAvailable: true,
  },
  {
    filter: REDACTION_STATUS_COMPLETE,
    label: REDACTION_STATUS_LABEL_COMPLETE,
    labelShort: REDACTION_STATUS_LABEL_COMPLETE,
    messageId: 'complete',
    colorHex: ACTIVE_PALETTE[REDACTION_STATUS_COMPLETE],
    colorName: 'Green',
    enabledByDefault: true,
    required: true,
    lockEditing: true,
    lockEditingAvailable: true,
  },
];

/** The five built-in status colors, in definition order. */
export const BUILT_IN_STATUS_COLORS: readonly string[] =
  REDACTION_STATUS_DEFINITIONS.map((definition) => definition.colorHex);

/** Preset palette for the custom-status color picker: five status hues plus purple. */
export const CUSTOM_STATUS_COLORS: readonly string[] = [
  ...BUILT_IN_STATUS_COLORS,
  '#8E24AA',
];

/**
 * Complementary second-row palette for the two-row picker: near-black,
 * cyan, yellow, a red, purple — each distinct from every built-in
 * status color and from the others (VE-24546). Cyan rather than a darker
 * blue: a hue shift separates it from Draft's blue, where a same-hue
 * shade change did not. The red slot depends on the palette: rose under
 * the dark palette (whose built-in Correction red #D32F2F is close to the
 * deep red #C62828), deep red under the original/updated palettes (whose
 * Correction rose is #f2827f).
 */
export const EXTRA_STATUS_COLORS = [
  '#263238',
  '#00ACC1',
  '#FDD835',
  ACTIVE_REDACTION_STATUS_PALETTE === 'dark' ? '#f2827f' : '#C62828',
  '#8E24AA',
] as const;

/**
 * Two-row color picker in the status drawer: top row = the built-in status
 * colors, bottom row = EXTRA_STATUS_COLORS, with the divider + custom
 * picker on the right end. When false the drawer shows the single-row
 * CUSTOM_STATUS_COLORS palette.
 */
export const USE_TWO_ROW_STATUS_PALETTE = true;

/**
 * Fallback color for a custom status whose stored `colorHex` is
 * missing/invalid, and the drawer's initial color for a new status: the
 * palette's blue slot, so the default is always a preset swatch.
 */
export const DEFAULT_CUSTOM_STATUS_COLOR: string =
  CUSTOM_STATUS_COLORS[1] ?? '#1976D2';

const statusByFilter = REDACTION_STATUS_DEFINITIONS.reduce(
  (acc, item) => {
    acc[item.filter] = item;
    return acc;
  },
  {} as Record<RedactionStatusFilter, StatusDefinition>
);

const statusByLabel = REDACTION_STATUS_DEFINITIONS.reduce(
  (acc, item) => {
    acc[item.label.toLowerCase()] = item;
    return acc;
  },
  {} as Record<string, StatusDefinition>
);

export const DEFAULT_REDACTION_STATUS_FILTER = REDACTION_STATUS_TO_DO;
export const DEFAULT_REDACTION_STATUS_LABEL = REDACTION_STATUS_LABEL_TO_DO;

export const isRedactionStatusFilter = (
  value: unknown
): value is RedactionStatusFilter =>
  typeof value === 'string' &&
  REDACTION_STATUS_FILTERS.includes(value as RedactionStatusFilter);

export const isRedactionStatusLabel = (
  value: unknown
): value is RedactionStatusLabel =>
  typeof value === 'string' &&
  REDACTION_STATUS_LABELS.includes(value as RedactionStatusLabel);

export const normalizeRedactionStatusFilter = (
  value: unknown
): RedactionStatusFilter => {
  if (typeof value !== 'string') {
    return DEFAULT_REDACTION_STATUS_FILTER;
  }
  const normalized = value.trim().toLowerCase();
  if (isRedactionStatusFilter(normalized)) {
    return normalized;
  }
  const byLabel = statusByLabel[normalized];
  return byLabel?.filter || DEFAULT_REDACTION_STATUS_FILTER;
};

/**
 * Resolves canonical status key:
 * - well-known labels/filters => well-known filter key
 * - unknown strings => preserved custom key (normalized to lowercase)
 * - non-string/empty => undefined
 */
export const resolveRedactionStatusKey = (
  value: unknown
): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const normalized = trimmed.toLowerCase();
  if (isRedactionStatusFilter(normalized)) {
    return normalized;
  }
  const byLabel = statusByLabel[normalized];
  if (byLabel) {
    return byLabel.filter;
  }
  return normalized;
};

export const normalizeRedactionStatusLabel = (
  value: unknown
): RedactionStatusLabel => {
  if (typeof value !== 'string') {
    return DEFAULT_REDACTION_STATUS_LABEL;
  }
  const normalized = value.trim().toLowerCase();
  const byLabel = statusByLabel[normalized];
  if (byLabel) {
    return byLabel.label;
  }
  if (isRedactionStatusFilter(normalized)) {
    return statusByFilter[normalized].label;
  }
  return DEFAULT_REDACTION_STATUS_LABEL;
};

export const getRedactionStatusMessageId = (
  value: unknown,
  useShortPendingReviewLabel = false
) => {
  const filter = normalizeRedactionStatusFilter(value);
  const definition = statusByFilter[filter];
  if (useShortPendingReviewLabel && definition.shortMessageId) {
    return definition.shortMessageId;
  }
  return definition.messageId;
};

// Returns the short display label for a redaction status — used in
// space-constrained spots like the case folder status badge and the column
// header dropdown. Falls back to the full label when no short variant exists.
export const getRedactionStatusLabelShort = (value: unknown): string => {
  const filter = normalizeRedactionStatusFilter(value);
  const definition = statusByFilter[filter];
  return definition.labelShort || definition.label;
};

export const REDACTION_STATUS_COLOR_MAP: Readonly<
  Record<RedactionStatusFilter, string>
> = Object.freeze(
  REDACTION_STATUS_DEFINITIONS.reduce(
    (acc, def) => {
      acc[def.filter] = def.colorHex;
      return acc;
    },
    {} as Record<RedactionStatusFilter, string>
  )
);

/**
 * Minimal shape for looking up customer-defined statuses when resolving
 * display color / lock policy (VE-24546). Matches `RedactionCustomStatus`
 * without importing the redact-app module from common-app.
 */
export interface CustomRedactionStatusLookup {
  readonly statusKey: string;
  readonly statusLabel: string;
  readonly enabled: boolean;
  readonly lockEditing: boolean;
  readonly colorHex: string;
  /** Soft-deleted rows still resolve (legacy-tagged files keep their attributes) but lose label-collision ties to live rows. */
  readonly isDeleted?: boolean;
}

/**
 * Finds a custom status for a stored tag value.
 * Prefer `statusKey` (UUID) match so a custom row never collapses into a
 * built-in with the same display label. Label match is only used when the
 * value is not itself a well-known filter/label (legacy rows).
 */
export const findCustomRedactionStatus = (
  value: unknown,
  customStatuses?: readonly CustomRedactionStatusLookup[] | null
): CustomRedactionStatusLookup | undefined => {
  if (typeof value !== 'string' || !value.trim() || !customStatuses?.length) {
    return undefined;
  }
  const rawLower = value.trim().toLowerCase();
  const byKey = customStatuses.find(
    (status) => status.statusKey.toLowerCase() === rawLower
  );
  if (byKey) {
    return byKey;
  }
  // Built-in filter/label wins over a legacy custom that shared the same name.
  if (isRedactionStatusFilter(rawLower) || statusByLabel[rawLower]) {
    return undefined;
  }
  // Deleted rows free their name for reuse, so on a label collision the
  // live row wins; the deleted one still resolves when it is the only match
  // (a legacy label-stored tag from before its deletion).
  const byLabel = (status: CustomRedactionStatusLookup) =>
    status.statusLabel.trim().toLowerCase() === rawLower;
  return (
    customStatuses.find((status) => !status.isDeleted && byLabel(status)) ??
    customStatuses.find(byLabel)
  );
};

/** True when `value` resolves to one of the five product statuses. */
export const isWellKnownRedactionStatus = (value: unknown): boolean => {
  const key = resolveRedactionStatusKey(value);
  return !!key && isRedactionStatusFilter(key);
};

/**
 * True when a stored tag value is neither a live custom status nor a
 * well-known built-in — typically a deleted custom statusKey (UUID).
 * Display collapses these to UNKNOWN; data on the TDO is left unchanged.
 */
export const isOrphanRedactionStatus = (
  value: unknown,
  customStatuses?: readonly CustomRedactionStatusLookup[] | null
): boolean => {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }
  if (findCustomRedactionStatus(value, customStatuses)) {
    return false;
  }
  return !isWellKnownRedactionStatus(value);
};

/**
 * Placeholder for a status we cannot resolve *yet*. Not i18n'd — it is a dash,
 * not a word.
 */
export const REDACTION_STATUS_UNRESOLVED_LABEL = '—';

/**
 * True when a status value cannot be classified yet because the org's custom
 * statuses have not loaded.
 *
 * Distinguishes "we don't know" from "this status was deleted" (VE-24546).
 * `isOrphanRedactionStatus` cannot tell them apart — a live custom status is
 * stored as its `statusKey` UUID, which looks identical to a deleted one until
 * the fetch lands. Callers that render "Unknown" must check this first, or a slow
 * or failed fetch labels every custom status on the dashboard as deleted.
 */
export const isUnresolvedRedactionStatus = (
  value: unknown,
  statusesLoaded: boolean
): boolean => {
  if (statusesLoaded || typeof value !== 'string' || !value.trim()) {
    return false;
  }
  return !isWellKnownRedactionStatus(value);
};

/**
 * Use built-in i18n for the pill when the value is a well-known status and
 * not a custom row keyed by UUID. Orphans use `statusUnknown` at the call
 * site (not a well-known filter message id).
 */
export const shouldUseBuiltInStatusI18n = (
  value: unknown,
  customStatuses?: readonly CustomRedactionStatusLookup[] | null
): boolean =>
  !findCustomRedactionStatus(value, customStatuses) &&
  isWellKnownRedactionStatus(value);

export const getRedactionStatusColorHex = (
  value: unknown,
  customStatuses?: readonly CustomRedactionStatusLookup[] | null
): string => {
  const custom = findCustomRedactionStatus(value, customStatuses);
  if (custom?.colorHex) {
    return custom.colorHex;
  }
  const key = resolveRedactionStatusKey(value);
  if (key && isRedactionStatusFilter(key)) {
    return statusByFilter[key].colorHex;
  }
  // Orphaned custom key/label (status deleted) — UNKNOWN chip (color TBD).
  if (isOrphanRedactionStatus(value, customStatuses)) {
    return UNKNOWN_REDACTION_STATUS_COLOR;
  }
  return statusByFilter[DEFAULT_REDACTION_STATUS_FILTER].colorHex;
};

export const getRedactionStatusColorName = (value: unknown): string => {
  const filter = normalizeRedactionStatusFilter(value);
  return statusByFilter[filter].colorName;
};

export const getDefaultRedactionStatusSettings = (): RedactionStatusSettings =>
  REDACTION_STATUS_DEFINITIONS.reduce((acc, statusDefinition, index) => {
    acc[statusDefinition.filter] = {
      enabled: statusDefinition.enabledByDefault,
      lockEditing: statusDefinition.lockEditing,
      // Default order follows the definition list (To Do 0, Draft 1, …).
      displayOrder: index,
    };
    return acc;
  }, {} as RedactionStatusSettings);

export const normalizeRedactionStatusSettings = (
  input: Partial<RedactionStatusSettings> | undefined | null
): RedactionStatusSettings => {
  const defaults = getDefaultRedactionStatusSettings();

  for (const definition of REDACTION_STATUS_DEFINITIONS) {
    const current = input?.[definition.filter];
    const required = !!definition.required;
    const lockEditingAvailable = definition.lockEditingAvailable;

    const enabled =
      required || typeof current?.enabled !== 'boolean'
        ? defaults[definition.filter].enabled
        : current.enabled;

    // Pinned keep their fixed slot; others use the persisted order or default.
    const displayOrder = isPinnedRedactionStatusFilter(definition.filter)
      ? defaults[definition.filter].displayOrder
      : typeof current?.displayOrder === 'number'
        ? current.displayOrder
        : defaults[definition.filter].displayOrder;

    defaults[definition.filter] = {
      enabled,
      lockEditing:
        enabled && lockEditingAvailable
          ? typeof current?.lockEditing === 'boolean'
            ? current.lockEditing
            : defaults[definition.filter].lockEditing
          : false,
      displayOrder,
    };
  }

  return defaults;
};

export const getResolvedRedactionStatusDefinitions = (
  settings: Partial<RedactionStatusSettings> | undefined = undefined
): ReadonlyArray<ResolvedStatusDefinition> => {
  const normalized = normalizeRedactionStatusSettings(
    settings || getDefaultRedactionStatusSettings()
  );

  return REDACTION_STATUS_DEFINITIONS.map((definition) => ({
    ...definition,
    enabled: normalized[definition.filter].enabled,
    lockEditing: normalized[definition.filter].lockEditing,
  }));
};

export const getEnabledRedactionStatusDefinitions = (
  settings: Partial<RedactionStatusSettings> | undefined = undefined
): ReadonlyArray<ResolvedStatusDefinition> =>
  getResolvedRedactionStatusDefinitions(settings).filter((s) => s.enabled);

export const isRedactionStatusEnabled = (
  value: unknown,
  settings: Partial<RedactionStatusSettings> | undefined = undefined,
  customStatuses?: readonly CustomRedactionStatusLookup[] | null
): boolean => {
  const custom = findCustomRedactionStatus(value, customStatuses);
  if (custom) {
    return custom.enabled;
  }
  const key = resolveRedactionStatusKey(value);
  if (!key || !isRedactionStatusFilter(key)) {
    return false;
  }
  const normalized = normalizeRedactionStatusSettings(
    settings || getDefaultRedactionStatusSettings()
  );
  return normalized[key].enabled;
};

/**
 * Whether the current status forbids editing.
 *
 * `statusesLoaded` is **required** because this decides authorization, so an
 * unknown answer must not read as "unlocked" (VE-24546). A custom status is
 * stored on the TDO as its `statusKey` UUID, which is not a built-in filter —
 * so with `customStatuses` still empty the UUID looks orphaned and the lock
 * silently drops. While `statusesLoaded` is false an unrecognized value fails
 * *closed* instead.
 *
 * Deliberately a required positional argument rather than an optional options
 * bag: as an optional field, omitting it produced `undefined === false` → false
 * → unlocked, i.e. the safe answer needed the caller to opt in, and TypeScript
 * could not flag a new call site that forgot. Required means the compiler
 * catches the fifth caller. Matches `isUnresolvedRedactionStatus`'s shape.
 *
 * Once statuses are loaded an unrecognized value is a genuine orphan (its
 * custom status was deleted), and orphans stay unlocked on purpose — otherwise
 * deleting a status would permanently freeze every file still tagged with it.
 */
export const isRedactionStatusLockEdits = (
  value: unknown,
  settings: Partial<RedactionStatusSettings> | undefined,
  customStatuses: readonly CustomRedactionStatusLookup[] | null | undefined,
  statusesLoaded: boolean
): boolean => {
  const custom = findCustomRedactionStatus(value, customStatuses);
  if (custom) {
    return custom.enabled && custom.lockEditing;
  }
  const key = resolveRedactionStatusKey(value);
  if (!key || !isRedactionStatusFilter(key)) {
    // Not a built-in and not a known custom row: lock unless we positively know
    // the custom statuses loaded. `!key` (no status at all) never locks.
    return !statusesLoaded && !!key;
  }
  const normalized = normalizeRedactionStatusSettings(
    settings || getDefaultRedactionStatusSettings()
  );
  return normalized[key].enabled && normalized[key].lockEditing;
};

/**
 * Human-readable label: custom rows use stored `statusLabel`; well-known rows
 * resolve from `statusKey` via `REDACTION_STATUS_DEFINITIONS` (so copy can ship in app/i18n without SDO migrations).
 */
export const getRedactionStatusDisplayLabel = (params: {
  readonly statusKey?: unknown;
  readonly statusLabel?: unknown;
  readonly isCustomStatus?: boolean;
}): string => {
  if (
    params.isCustomStatus &&
    typeof params.statusLabel === 'string' &&
    params.statusLabel.trim()
  ) {
    return params.statusLabel.trim();
  }
  const resolvedKey = resolveRedactionStatusKey(
    params.statusKey || params.statusLabel
  );
  if (resolvedKey && isRedactionStatusFilter(resolvedKey)) {
    return statusByFilter[resolvedKey].label;
  }
  if (params.isCustomStatus && resolvedKey) {
    return resolvedKey;
  }
  return DEFAULT_REDACTION_STATUS_LABEL;
};

/**
 * Resolves the label shown on status pills/menus for a tag value, looking up
 * org custom statuses when the value is not one of the five well-known ones.
 *
 * `fallbackName` is the human-readable name frozen on the tag at write time
 * (`redactionStatus`) — VE-24546 stores it alongside the `redactionStatusKey`
 * UUID so a *deleted* custom status can still show its last-known name instead
 * of "Unknown". When a value is orphaned and a non-empty `fallbackName` is
 * supplied, that name wins; otherwise orphans fall back to "Unknown" (legacy
 * tags that only carry a UUID never had a name to fall back to).
 */
export const resolveRedactionStatusDisplayLabel = (
  value: unknown,
  customStatuses?: readonly CustomRedactionStatusLookup[] | null,
  fallbackName?: string | null
): string => {
  const custom = findCustomRedactionStatus(value, customStatuses);
  if (custom) {
    return custom.statusLabel;
  }
  const key = resolveRedactionStatusKey(value);
  if (key && isRedactionStatusFilter(key)) {
    return statusByFilter[key].label;
  }
  // Orphaned custom statusKey/label after the definition was deleted: prefer the
  // name frozen on the tag, else Unknown.
  if (typeof value === 'string' && value.trim()) {
    return typeof fallbackName === 'string' && fallbackName.trim()
      ? fallbackName.trim()
      : REDACTION_STATUS_LABEL_UNKNOWN;
  }
  return DEFAULT_REDACTION_STATUS_LABEL;
};

/**
 * The canonical in-app status identity for a stored `in redaction` tag
 * (VE-24546). The tag carries a human-readable `redactionStatus` name plus, for
 * custom statuses, a stable `redactionStatusKey` UUID. The identity is the key
 * when present, else the name — which is exactly what legacy tags stored in
 * `redactionStatus` (a built-in label, or a bare custom UUID), so old data
 * resolves unchanged. Everything downstream (matching, color, lock, filter)
 * keys off this identity, not the display name.
 */
export const getRedactionStatusIdentity = (tag: {
  readonly redactionStatus?: unknown;
  readonly redactionStatusKey?: unknown;
}): string | undefined => {
  const key =
    typeof tag.redactionStatusKey === 'string' && tag.redactionStatusKey.trim()
      ? tag.redactionStatusKey.trim()
      : undefined;
  if (key) {
    return key;
  }
  return typeof tag.redactionStatus === 'string' && tag.redactionStatus.trim()
    ? tag.redactionStatus
    : undefined;
};

/** The two fields persisted on the `in redaction` tag for a chosen status. */
export interface RedactionStatusTagWriteFields {
  /** Human-readable name (what other aiWARE apps display). */
  readonly redactionStatus: string;
  /** Stable UUID; written for custom statuses only, omitted for built-ins. */
  readonly redactionStatusKey?: string;
}

/**
 * Builds the `{ redactionStatus, redactionStatusKey? }` pair to persist on the
 * TDO tag from a picker selection (VE-24546). `displayLabel` becomes the
 * human-readable `redactionStatus`; for custom statuses the `statusKey` UUID is
 * also stored so a rename or a built-in-label collision can never steal the
 * file's status identity. Built-ins omit the key — legacy data never had one
 * and their label round-trips on its own.
 */
export const getRedactionStatusTagWriteFields = (selection: {
  readonly value: string;
  readonly displayLabel: string;
  readonly isCustom: boolean;
}): RedactionStatusTagWriteFields => {
  const key = selection.value.trim();
  return {
    redactionStatus: selection.displayLabel,
    ...(selection.isCustom && key ? { redactionStatusKey: key } : {}),
  };
};

/**
 * Merges the status write fields onto an existing (or new) `in redaction` tag,
 * always dropping any pre-existing `redactionStatusKey` first so a custom →
 * built-in change cannot leave a stale UUID behind. Preserves every other field
 * on the tag (e.g. `value`, trash stamps). Used by all TDO write paths.
 */
export const mergeRedactionStatusTagFields = <T extends object>(
  existingTag: T | null | undefined,
  fields: RedactionStatusTagWriteFields
): Omit<T, keyof RedactionStatusTagWriteFields> &
  RedactionStatusTagWriteFields => {
  const rest = { ...(existingTag ?? {}) } as Record<string, unknown>;
  delete rest.redactionStatusKey;
  return {
    ...(rest as Omit<T, keyof RedactionStatusTagWriteFields>),
    redactionStatus: fields.redactionStatus,
    ...(fields.redactionStatusKey
      ? { redactionStatusKey: fields.redactionStatusKey }
      : {}),
  };
};

/**
 * Write fields for a programmatic status change that only knows the canonical
 * identity string (e.g. the To Do → Draft auto-promote, or a rollback). Custom
 * callers should prefer `getRedactionStatusTagWriteFields` with the picker
 * selection so the human name and key are exact; this derives a built-in label
 * from the identity and never emits a key (built-ins don't carry one).
 */
export const getBuiltInRedactionStatusTagWriteFields = (
  identity: string
): RedactionStatusTagWriteFields => ({
  redactionStatus: normalizeRedactionStatusLabel(identity),
});

/**
 * Reconstructs a STATUS_CHANGE payload (`status` identity + `statusLabel` +
 * custom `statusKey`) from just an identity string, using the org's custom
 * statuses to recover a renamed custom's current label (VE-24546). Used where
 * only the identity is available — e.g. replaying a pending locked status after
 * a save completes.
 */
export const getRedactionStatusChangeFieldsFromIdentity = (
  identity: string,
  customStatuses?: readonly CustomRedactionStatusLookup[] | null
): { status: string; statusLabel: string; statusKey?: string } => {
  const custom = findCustomRedactionStatus(identity, customStatuses);
  if (custom) {
    return {
      status: custom.statusKey,
      statusLabel: custom.statusLabel,
      statusKey: custom.statusKey,
    };
  }
  return {
    status: identity,
    statusLabel: normalizeRedactionStatusLabel(identity),
  };
};

/**
 * True when `value` is the well-known To Do status (not a custom status whose
 * label happened to normalize poorly). Used by To Do → Draft auto-promote.
 */
export const isWellKnownRedactionStatusToDo = (value: unknown): boolean =>
  resolveRedactionStatusKey(value) === REDACTION_STATUS_TO_DO;

/**
 * True when a file's stored `redactionStatus` tag matches a filter picker
 * value (well-known filter key or custom status key/label). Missing tags
 * count as To Do.
 */
export const matchesRedactionStatusFilter = (
  fileStatus: unknown,
  filterValue: string,
  customStatuses?: readonly CustomRedactionStatusLookup[] | null
): boolean => {
  if (filterValue === 'all') {
    return true;
  }
  const normalizedFilter = filterValue.trim().toLowerCase();
  if (!normalizedFilter) {
    return true;
  }
  if (
    (normalizedFilter === REDACTION_STATUS_TO_DO ||
      normalizedFilter === DEFAULT_REDACTION_STATUS_LABEL.toLowerCase()) &&
    (isNil(fileStatus) ||
      (typeof fileStatus === 'string' && !fileStatus.trim()))
  ) {
    return true;
  }
  if (typeof fileStatus !== 'string' || !fileStatus.trim()) {
    return false;
  }
  // Custom statusKey (UUID) filter — compare keys/labels via lookup.
  const filterCustom = findCustomRedactionStatus(filterValue, customStatuses);
  const fileCustom = findCustomRedactionStatus(fileStatus, customStatuses);
  if (filterCustom || fileCustom) {
    if (filterCustom && fileCustom) {
      return filterCustom.statusKey === fileCustom.statusKey;
    }
    // Filter is a statusKey/label for a live custom; file may still carry the
    // same statusKey after a rename, or a legacy label.
    if (filterCustom) {
      const fileLower = fileStatus.trim().toLowerCase();
      return (
        filterCustom.statusKey.toLowerCase() === fileLower ||
        filterCustom.statusLabel.trim().toLowerCase() === fileLower
      );
    }
    return false;
  }
  const fileKey =
    resolveRedactionStatusKey(fileStatus) || fileStatus.trim().toLowerCase();
  const filterKey = resolveRedactionStatusKey(filterValue) || normalizedFilter;
  return fileKey === filterKey;
};

/**
 * First **enabled** well-known status in product preference order, for new file SDOs
 * (create TDO, add external TDO, trim) when org settings disable some statuses.
 */
export const getInitialWellKnownFileRedactionUpsertFields = (
  settings?: Partial<RedactionStatusSettings> | null
): {
  readonly statusLabel: RedactionStatusLabel;
  readonly statusKey: RedactionStatusFilter;
  readonly isCustomStatus: false;
} => {
  const normalized = normalizeRedactionStatusSettings(settings);
  const preferenceOrder: readonly RedactionStatusFilter[] = [
    REDACTION_STATUS_TO_DO,
    REDACTION_STATUS_DRAFT,
    REDACTION_STATUS_PENDING_REVIEW,
    REDACTION_STATUS_CORRECTION_NEEDED,
    REDACTION_STATUS_COMPLETE,
  ];
  for (const filter of preferenceOrder) {
    if (normalized[filter]?.enabled) {
      return {
        statusLabel: statusByFilter[filter].label,
        statusKey: filter,
        isCustomStatus: false,
      };
    }
  }
  return {
    statusLabel: statusByFilter[DEFAULT_REDACTION_STATUS_FILTER].label,
    statusKey: DEFAULT_REDACTION_STATUS_FILTER,
    isCustomStatus: false,
  };
};
